import { createGlobalStyle } from 'styled-components'

/**
 * Global print styles for PDF export
 * Must be included when the modal is open to enable proper print formatting
 */
export const PrintStyles = createGlobalStyle`
  @media print {
    /* Page setup - A4 landscape with margins */
    @page {
      size: A4;
      margin: 1.5cm 1cm;
    }

    /* Reset html/body for printing */
    html,
    body {
      height: auto !important;
      overflow: visible !important;
      background: white !important;
    }

    /* Hide everything except the print modal content */
    body > *:not(.print-modal-portal),
    #root > *:not(.print-modal-portal),
    #root {
      display: none !important;
    }

    body > .print-modal-portal {
      display: block !important;
    }

    /* Reset modal for printing */
    .print-modal-portal,
    .print-modal-portal * {
      overflow: visible !important;
    }

    .print-modal-portal {
      position: static !important;
      width: 100% !important;
      height: auto !important;
      background: transparent !important;
      z-index: auto !important;
    }

    .print-modal-overlay {
      position: static !important;
      display: block !important;
      background: transparent !important;
      backdrop-filter: none !important;
    }

    .print-modal-content {
      position: static !important;
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      height: auto !important;
      max-height: none !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      background: transparent !important;
    }

    /* Main report container - fixed 1200px width */
    .print-report {
      display: block !important;
      width: 1200px !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    /* Reset zoom on report container for print */
    .print-modal-content > div {
      zoom: 1 !important;
    }

    .print-report {
      zoom: 1 !important;
    }

    /* Hide screen-only elements */
    .modal-backdrop,
    .modal-header,
    .modal-actions,
    .screen-only,
    .close-button,
    .zoom-controls {
      display: none !important;
    }

    /* Force background colors to print */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* ================================
     * PAGE BREAK UTILITIES
     * ================================ */

    /* Cover page - preserve screen layout */
    .print-page-preview {
      display: flex !important;
      flex-direction: column !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
    }

    /* Cover content fills available space */
    .print-cover-content {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      height: 100% !important;
      overflow: hidden !important;
    }

    /* Section page break - starts on new page */
    .print-section-page {
      break-before: page !important;
      page-break-before: always !important;
      padding: 20px 26px !important;
      box-sizing: border-box !important;
    }

    /* Page that should break after */
    .print-page-break-after {
      break-after: page !important;
      page-break-after: always !important;
    }

    /* Last page wrapper */
    .print-last-page {
      break-before: page !important;
      page-break-before: always !important;
      display: flex !important;
      flex-direction: column !important;
      height: calc(100vh - 3cm) !important;
      min-height: calc(100vh - 3cm) !important;
      box-sizing: border-box !important;
    }

    /* Content that should not break */
    .print-no-break {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }

    /* Prevent orphan page breaks for headings */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      break-after: avoid;
      page-break-after: avoid;
    }

    /* Tables should not break inside rows */
    table {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* ================================
     * CHART AND CANVAS HANDLING
     * ================================ */

    /* Ensure canvases fit within their containers (exclude Mapbox canvas) */
    .print-report canvas:not(.mapboxgl-canvas) {
      max-width: 100% !important;
    }

    /* Mapbox map container - preserve dimensions for correct marker positioning */
    .print-map-container {
      overflow: hidden !important;
    }

    /* Force ECharts containers to respect bounds */
    .print-report div[_echarts_instance_],
    .print-report div[style*='position: relative'][style*='width:']:not(.print-map-container) {
      max-width: 100% !important;
      width: 100% !important;
    }

    /* Ensure all chart wrapper divs respect container bounds (exclude photo wrapper and map) */
    .print-report div > div > div[style*='width']:not(.print-user-photo-wrapper):not(.print-map-container) {
      max-width: 100% !important;
      width: 100% !important;
    }

    /* ================================
     * DISCLAIMER / FOOTER
     * ================================ */

    .print-disclaimer {
      margin-top: auto !important;
    }

    /* ================================
     * COMPACT LIST STYLES
     * ================================ */

    .print-compact-table {
      width: 100% !important;
      border-collapse: collapse !important;
    }

    .print-compact-table th,
    .print-compact-table td {
      padding: 6px 10px !important;
      border-bottom: 1px solid #e2e8f0 !important;
    }

    .print-compact-table th {
      background: #f8fafc !important;
      font-weight: 600 !important;
      text-align: left !important;
    }

    .print-compact-table tr:nth-child(even) {
      background: #fafafa !important;
    }

    /* ================================
     * HEADER IMAGES (Logo & User Photo)
     * ================================ */

    /* User photo wrapper - maintains circular shape */
    .print-user-photo-wrapper {
      width: 45px !important;
      height: 45px !important;
      min-width: 45px !important;
      min-height: 45px !important;
      max-width: 45px !important;
      max-height: 45px !important;
      border-radius: 50% !important;
      overflow: hidden !important;
      flex-shrink: 0 !important;
    }

    /* User photo image inside wrapper */
    .print-user-photo {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      display: block !important;
    }

    /* Company logo maintains size */
    .print-company-logo {
      height: 64px !important;
      max-width: 192px !important;
      object-fit: contain !important;
    }

    /* ================================
     * REMOVE INTERACTIVE EFFECTS
     * ================================ */

    .print-report *:hover {
      transform: none !important;
      box-shadow: none !important;
    }

    /* Remove link underlines in print */
    .print-report a {
      text-decoration: none !important;
      color: inherit !important;
    }
  }
`
